import type { EventTemplate } from "@/models/template"
import { defineStore } from "pinia"
import { computed, ref } from "vue"

export const useTemplateStore = defineStore("template", () => {
  const eventTemplate = ref<EventTemplate | null>(null)

  const themeJson = computed(() => eventTemplate.value?.themeJson)
  const templateCss = computed(() => eventTemplate.value?.templateCss)
  const baseFontLabel = computed(() => themeJson.value?.tokens?.["--template-base-font"] ?? eventTemplate.value?.baseFontLabel)
  const baseFontValue = computed(() => themeJson.value?.typography?.baseFontValue ?? eventTemplate.value?.baseFontValue)
  const headingFontLabel = computed(() => themeJson.value?.tokens?.["--template-heading-font"] ?? eventTemplate.value?.headingFontLabel)
  const headingFontValue = computed(() => themeJson.value?.typography?.headingFontValue ?? eventTemplate.value?.headingFontValue)
  const customFooterLinks = computed(() => eventTemplate.value?.customFooterLinks)
  const customHeaderImage = computed(() => eventTemplate.value?.pathToHeaderImage)
  const customFooterImage = computed(() => eventTemplate.value?.pathToFooterImage)

  /**
   * Set the template in the store
   */
  function setTemplate(template: EventTemplate | null) {
    eventTemplate.value = template
  }

  /**
   * Set guest layout styles from JSON theme tokens
   * e.g. .guest-layout { --property: value; }
   */
  function setGuestLayoutStyle(tokens: Record<string, string>) {
    const styleElement = document.createElement("style")
    styleElement.id = "guest-layout-template-styles"

    // Build CSS variables string from theme tokens
    const cssVariables = Object.entries(tokens)
      .map(([key, value]) => `${key}: ${value};`)
      .join(" ")

    styleElement.textContent = `.guest-layout {${cssVariables}}`

    document.head.appendChild(styleElement)
  }

  /** @deprecated Legacy CSS-based event template model for legacy templates */
  function setTemplateCss(templateCss: string) {
    const styleElement = document.createElement("style")
    styleElement.id = "guest-layout-template-styles"

    // Extract css variables from :root block
    const cssVariables = templateCss
      .replace(/^\s*:root\s*\{\s*/, "")
      .replace(/\s*\}\s*$/, "")
      .trim()

    styleElement.textContent = `.guest-layout {${cssVariables}}`

    document.head.appendChild(styleElement)
  }

  /**
   * Apply the template styles to the guest layout
   */
  function applyTemplateStyles() {
    clearTemplateStyles()

    if (themeJson.value?.tokens) {
      setGuestLayoutStyle(themeJson.value.tokens)
    }
    else if (templateCss.value) {
      setTemplateCss(templateCss.value)
    }
  }

  /**
   * Clear the template styles from the guest layout
   */
  function clearTemplateStyles() {
    const existingStyle = document.getElementById("guest-layout-template-styles")
    if (existingStyle) {
      existingStyle.remove()
    }
  }

  /**
   * Clear the template from the store
   */
  function clearTemplate() {
    eventTemplate.value = null
    clearTemplateStyles()
  }

  return {
    // state
    eventTemplate,

    // computed properties
    templateCss,
    baseFontLabel,
    baseFontValue,
    headingFontLabel,
    headingFontValue,
    customFooterLinks,
    customHeaderImage,
    customFooterImage,

    // actions
    setTemplate,
    clearTemplate,
    applyTemplateStyles,
    clearTemplateStyles,
    setGuestLayoutStyle,
  }
})
